import { useState, useEffect } from 'react';

import { CaseConfig } from '@/modules/case/types';
import { captureSentry } from '@/utils/sentryHelper';

export const useCaseDetails = (configUrl: string | null) => {
  const [details, setDetails] = useState<CaseConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!configUrl) return;
    const loadCaseDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(configUrl);
        if (!response.ok) {
          throw new Error(`Failed to load case details: ${response.statusText}`);
        }

        const caseConfig = (await response.json()) as CaseConfig;

        // Chercher la première slide de type "anamnese"
        const anamneseSlide = caseConfig.slides.find((slide) => slide.type === 'anamnese');
        if (!anamneseSlide) {
          throw new Error('No anamnese slide found in case configuration');
        }

        // Extraire l'item de type "anamnese"
        const anamneseItem = anamneseSlide.items.find((item) => item.type === 'anamnese');
        if (!anamneseItem) {
          throw new Error('No anamnese item found in anamnese slide');
        }

        setDetails(caseConfig);
        setLoading(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setLoading(false);
        console.error('Error loading case details:', err);
        captureSentry({
          title: 'Failed to load case details',
          error: err,
          detail: { configUrl },
        });
      }
    };

    void loadCaseDetails();
  }, [configUrl]);

  return { details, loading, error };
};
